import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './CoreServicesSection.css';

// Image protection handlers
const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
};

const handleDragStart = (e: any) => {
    e.preventDefault();
    return false;
};

const coreServices = [
    {
        id: 'wedding',
        title: 'WEDDING FILMS',
        subtitle: 'CINEMATOGRAPHY',
        description: 'Capturing the raw emotion and timeless beauty of your special day. We craft cinematic love stories that you will cherish forever.',
        image: 'https://res.cloudinary.com/dhqmcmekn/image/upload/v1764784134/25_cy7kk3_obksed.jpg',
        galleryPath: '/wedding-gallery'
    },
    {
        id: 'prewedding',
        title: 'PRE-WEDDING',
        subtitle: 'EDITORIAL',
        description: 'Artistic and intimate sessions designed to celebrate your chemistry before the big day. A perfect blend of style and romance.',
        image: 'https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754959/DSC_0657_mr2sua.webp',
        galleryPath: '/prewedding-gallery'
    },
    {
        id: 'rice-ceremony',
        title: 'RICE CEREMONY',
        subtitle: 'TRADITIONAL',
        description: 'Honoring cultural heritage with sacred rituals. We document the blessing and abundance of this traditional celebration with reverence and artistry.',
        image: 'https://res.cloudinary.com/dhqmcmekn/image/upload/v1764472452/SNL00585-Edited_h1l39m.webp',
        galleryPath: '/rice-ceremony-gallery'
    },
    {
        id: 'model-photoshoot',
        title: 'MODEL PHOTOSHOOT',
        subtitle: 'COMMERCIAL',
        description: 'Professional portfolio and commercial photography sessions. We create stunning visual narratives that showcase your unique style and professional brand.',
        image: 'https://res.cloudinary.com/dhqmcmekn/image/upload/v1764801249/SNL09644-EDITED_cyhr3s.webp',
        galleryPath: '/baby-shower-gallery'
    }
];

const CoreServicesSection: React.FC = () => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
    const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
    const sectionRef = useRef<HTMLDivElement>(null);
    const galleryRef = useRef<HTMLDivElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    };

    const cardVariants = {
        hidden: { 
            opacity: 0, 
            y: 60,
            scale: 0.95
        },
        visible: { 
            opacity: 1, 
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: [0.25, 1, 0.5, 1] as const
            }
        }
    };

    const carouselVariants = {
        hidden: { 
            opacity: 0, 
            x: 100,
            scale: 0.9
        },
        visible: { 
            opacity: 1, 
            x: 0,
            scale: 1,
            transition: {
                duration: 0.8,
                ease: [0.25, 1, 0.5, 1] as const
            }
        }
    };

    const controlsVariants = {
        hidden: { 
            opacity: 0, 
            y: 30
        },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 0.6,
                delay: 0.4,
                ease: [0.25, 1, 0.5, 1] as const
            }
        }
    };

    const ctaVariants = {
        hidden: { 
            opacity: 0, 
            y: 50,
            scale: 0.95
        },
        visible: { 
            opacity: 1, 
            y: 0,
            scale: 1,
            transition: {
                duration: 0.8,
                delay: 0.6,
                ease: [0.25, 1, 0.5, 1] as const
            }
        }
    };

    const handleSeePackages = () => {
        const servicesSection = document.getElementById('services');
        if (servicesSection) {
            servicesSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const handleGalleryClick = (galleryPath: string) => {
        navigate(galleryPath);
    };

    const handlePrevMobile = () => {
        setSwipeDirection('right');
        setCurrentMobileIndex((prev) => prev === 0 ? coreServices.length - 1 : prev - 1);
    };

    const handleNextMobile = () => {
        setSwipeDirection('left');
        setCurrentMobileIndex((prev) => (prev + 1) % coreServices.length);
    };

    const handleDotClick = (index: number) => {
        setCurrentMobileIndex(index);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        setTouchStart({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!touchStart) return;
        
        const touch = e.changedTouches[0];
        const touchEnd = { x: touch.clientX, y: touch.clientY };
        
        const deltaX = touchEnd.x - touchStart.x;
        const deltaY = Math.abs(touchEnd.y - touchStart.y);
        
        // Check if it's a horizontal swipe (more horizontal than vertical movement)
        // Reduced threshold from 50px to 30px for more responsive feel
        if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 30) {
            if (deltaX > 0) {
                // Swipe right - go to previous
                setSwipeDirection('right');
                handlePrevMobile();
            } else {
                // Swipe left - go to next
                setSwipeDirection('left');
                handleNextMobile();
            }
        }
        
        setTouchStart(null);
    };

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start end', 'end start'],
    });

    const titleY = useTransform(scrollYProgress, [0.05, 0.25], [isMobile ? 30 : 60, 0]);
    const titleOpacity = useTransform(scrollYProgress, [0.05, 0.25], [0, 1]);

    return (
        <section className="core-services-section" id="core-services" ref={sectionRef}>
            <div className="services-inner">
                <motion.div
                    className="services-header"
                    style={{ y: titleY, opacity: titleOpacity }}
                >
                    <p className="services-eyebrow">CORE EXPERTISE</p>
                    <h2 className="services-title">
                        VISUAL <span className="title-accent">MASTERY</span>
                    </h2>
                    <p className="services-subcopy">
                        Wedding cinematography, pre-wedding editorials, event coverage, and commercial branding — each crafted with cinematic excellence and artistic vision.
                    </p>
                </motion.div>

            <motion.div 
                ref={galleryRef}
                className="services-gallery"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, margin: "-100px" }}
            >
                {isMobile ? (
                    <>
                        <motion.div 
                            className="mobile-carousel-container" 
                            onTouchStart={handleTouchStart} 
                            onTouchEnd={handleTouchEnd}
                            variants={carouselVariants}
                        >
                            <AnimatePresence mode="sync">
                                <motion.div
                                    key={currentMobileIndex}
                                    className="service-panel mobile-carousel-item"
                                    onClick={() => handleGalleryClick(coreServices[currentMobileIndex].galleryPath)}
                                    initial={{ 
                                        opacity: 0, 
                                        x: swipeDirection === 'right' ? -300 : 300, 
                                        scale: 0.95 
                                    }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ 
                                        opacity: 0, 
                                        x: swipeDirection === 'right' ? 300 : -300, 
                                        scale: 0.95 
                                    }}
                                    transition={{ 
                                        duration: 0.35, 
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                        scale: { duration: 0.3 }
                                    }}
                                >
                                    <div className="service-bg-wrapper">
                                        <img 
                                            src={coreServices[currentMobileIndex].image} 
                                            alt={coreServices[currentMobileIndex].title} 
                                            className="service-bg"
                                            onContextMenu={handleContextMenu}
                                            onDragStart={handleDragStart}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        <div className="service-overlay" />
                                    </div>

                                    <div className="service-content">
                                        <div className="service-text-wrapper">
                                            <h3 className="service-title">{coreServices[currentMobileIndex].title}</h3>
                                            <p className="service-desc">{coreServices[currentMobileIndex].description}</p>
                                            <div className="service-gallery-btn" onClick={(e) => {
                                                e.stopPropagation();
                                                handleGalleryClick(coreServices[currentMobileIndex].galleryPath);
                                            }}>
                                                SEE GALLERY <span className="arrow-diagonal">↗</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="service-number">
                                        0{currentMobileIndex + 1}
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>

                        <motion.div className="mobile-carousel-controls" variants={controlsVariants}>
                            <button className="carousel-nav-btn prev-btn" onClick={handlePrevMobile}>
                                ←
                            </button>
                            <div className="carousel-dots">
                                {coreServices.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`carousel-dot ${index === currentMobileIndex ? 'active' : ''}`}
                                        onClick={() => handleDotClick(index)}
                                    />
                                ))}
                            </div>
                            <button className="carousel-nav-btn next-btn" onClick={handleNextMobile}>
                                →
                            </button>
                        </motion.div>
                    </>
                ) : (
                    coreServices.map((service, index) => (
                        <motion.div
                            key={service.id}
                            className={`service-panel ${hoveredId === service.id ? 'active' : ''}`}
                            onMouseEnter={() => setHoveredId(service.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            onClick={() => handleGalleryClick(service.galleryPath)}
                            variants={cardVariants}
                            custom={index}
                        >
                            <div className="service-bg-wrapper">
                                <img 
                                    src={service.image} 
                                    alt={service.title} 
                                    className="service-bg"
                                    onContextMenu={handleContextMenu}
                                    onDragStart={handleDragStart}
                                    style={{ pointerEvents: 'none' }}
                                />
                                <div className="service-overlay" />
                            </div>

                            <div className="service-content">
                                <div className="service-text-wrapper">
                                    <h3 className="service-title">{service.title}</h3>
                                    <p className="service-desc">{service.description}</p>
                                    <div className="service-gallery-btn" onClick={(e) => {
                                        e.stopPropagation();
                                        handleGalleryClick(service.galleryPath);
                                    }}>
                                        SEE GALLERY <span className="arrow-diagonal">↗</span>
                                    </div>
                                </div>
                            </div>

                            <div className="service-number">
                                0{coreServices.indexOf(service) + 1}
                            </div>
                        </motion.div>
                    ))
                )}
            </motion.div>

            <motion.div 
                ref={ctaRef}
                className="core-services-cta"
                variants={ctaVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, margin: "-50px" }}
            >
                <p className="cta-subtitle">READY TO CAPTURE YOUR MOMENTS?</p>
                <h3 className="cta-title">EXPLORE OUR PACKAGES</h3>
                <p className="cta-description">
                    Discover our carefully crafted packages designed to preserve your most precious memories with cinematic excellence.
                </p>
                <button className="cta-see-packages-btn" onClick={handleSeePackages}>
                    SEE PACKAGES <span className="cta-arrow">→</span>
                </button>
            </motion.div>
            </div>
        </section>
    );
};

export default CoreServicesSection;
